import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FundraisingPageFragment,
  PageStatus,
} from '../../../types/__generated__';
import config from '../../client/config';
import {
  AboutCampaignAndCharitySection,
  AboutCauseMini,
  ActivityTotals,
  Banner,
  DefaultLayout,
  DonationSummary,
  Editbar,
  EncourageInactivityModal,
  EventDetails,
  FeatureImage,
  FitnessSection,
  FundraisingCTA,
  FundraisingHelmet,
  FundraisingSocialButtons,
  GoToImmersiveGiving,
  IsPageOwner,
  IsQRCode,
  Page,
  PageAlert,
  PageOwnerAvatar,
  PageOwnerTitle,
  PageSection,
  Prompt,
  QRModal,
  QualarooLoader,
  Reason,
  Story,
  SupportersList,
  TeamAndCompany,
  TeamDetails,
  TimelinePageSection,
  TotaliserWrapper,
  Totals,
  StickySummary,
} from '../../components';
import { PageMetaContext } from '../../context';
import {
  buildQuery,
  useFlags,
  useNotNullableContext,
  usePageQuery,
} from '../../hooks';
import { useScript } from '../../hooks/useScript';
import Fragment from './FundraisingPageFragment.gql';

import styles from './FundraisingPage.scss';

const FundraisingPageQuery = buildQuery(Fragment);

export const FundraisingPage: React.FunctionComponent = () => {
  useScript('//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js', {
    async: true,
  });
  const ctaRef = useRef<HTMLDivElement>(null);
  const { data } = usePageQuery<FundraisingPageFragment>(FundraisingPageQuery);
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName } = useNotNullableContext(PageMetaContext);
  const { enableOtgImmersiveGiving } = useFlags();

  const name = data?.owner?.name ?? '';
  const eventGivingGroupId = data?.eventGivingGroupId;
  const totalRelatedCompanies = data?.relationships?.companies.totalCount ?? 0;
  const displayTeamAndCompany =
    totalRelatedCompanies > 0 ||
    data?.relationships?.teams.nodes[0]?.status === PageStatus.Active;

  return (
    <Page>
      <IsQRCode>
        <QRModal ownerName={name} />
      </IsQRCode>
      <PageAlert />
      <Banner />
      {eventGivingGroupId && (
        <IsPageOwner>
          <Prompt entityId={eventGivingGroupId.toString()} />
        </IsPageOwner>
      )}
      <Editbar
        adminLink={`${config.baseUri}/account/your-pages/${pageShortName}?pageId=${eventGivingGroupId}`}
      />
      <FundraisingHelmet />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <PageOwnerAvatar />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <TotaliserWrapper />
              <Totals />
            </>
          ),
          callsToAction: (
            <div ref={ctaRef} className={styles.ctaWrapper}>
              <FundraisingCTA />
            </div>
          ),
          titlesSupplementary: (
            <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
              <EventDetails />
              <Reason />
              <TeamDetails />
            </div>
          ),
          causeMini: <AboutCauseMini />,
          main: (
            <>
              <PageSection
                title={t('story.title')}
                className="jg-pages-fundraising__story"
                hasBorder={true}
              >
                <Story />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <FundraisingSocialButtons />
              </PageSection>
              <TimelinePageSection />
            </>
          ),
          aside: (
            <>
              {enableOtgImmersiveGiving &&
                data?.relationships?.campaigns.nodes[0]?.slug ===
                  'make-a-refugee-shelter-a-home' && <GoToImmersiveGiving />}
              <IsPageOwner>
                <PageSection>
                  <FitnessSection />
                </PageSection>
              </IsPageOwner>
              <ActivityTotals />
              <SupportersList
                noSupportersMessage={t(
                  'fundraising.noSupportersMessage',
                  `Become {{name}}'s first supporter by making a donation`,

                  { replace: { name } },
                )}
                displayDonateButton
              />
            </>
          ),
          teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
          contentFooterPrimary: <AboutCampaignAndCharitySection />,
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={['onlineAmount', 'offlineAmount', 'textDonations']}
            />
          ),
        }}
      />
      <StickySummary ref={ctaRef} />
      <EncourageInactivityModal />
      <QualarooLoader />
    </Page>
  );
};

FundraisingPage.displayName = 'FundraisingPage';

export default FundraisingPage;
